import { useApi } from "@/hooks";
import InviteOffersDialogVR from "@Components/app/notifications/InviteOffersDialogVR";
import { Actions, AppContext } from "@Context/common/general/context";
import { Api } from "@Shared/schemas";
import { InviteResponse } from "@Shared/schemas/accounts/inviteSchemas";
import { useCallback, useContext, useEffect, useState } from "react";
import { defineNotificationModule, INotificationResolverProps } from "../notification-service.types";
import { useAccountInfoResolved } from "../shared/use-account-info-resolved";
import { useExclusiveMode } from "../shared/use-exclusive-mode";
import { useNotificationCandidate } from "../use-notification-candidate";

const id = "PENDING_INVITES";

const isInviteStillPending = (invite: InviteResponse) => {
  const isExpired = invite.expires_at ? new Date(invite.expires_at).getTime() <= Date.now() : false;

  return invite.state === "pending" && !isExpired;
};

const PendingInvitesNotificationResolver = ({ priority }: INotificationResolverProps) => {
  const { state, dispatch } = useContext(AppContext);
  const accountInfoResolved = useAccountInfoResolved(state.user.authenticated);
  const isExclusiveMode = useExclusiveMode();
  const [pendingInvitesResolved, setPendingInvitesResolved] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<InviteResponse[]>([]);

  const { call: getInvites } = useApi({
    api: Api.Invites.GetInvitesForUser,
    request: {},
    onSuccess: ({ data }) => {
      const pendingInvites = (data || []).filter((invite: InviteResponse) => isInviteStillPending(invite));
      setPendingInvites(pendingInvites);
      setPendingInvitesResolved(true);
      dispatch({ type: Actions.CoreActions.UpdateState, payload: { user: { groupInvites: pendingInvites, groupInvitesResolved: true } } });
    },
    onError: () => {
      dispatch({ type: Actions.CoreActions.UpdateState, payload: { user: { pendingInvitesResolved: true } } });
    },
  });

  useEffect(() => {
    if (!accountInfoResolved) {
      setPendingInvites([]);
      setPendingInvitesResolved(false);
      return;
    }

    getInvites();
  }, [accountInfoResolved, getInvites]);

  const handleClose = useCallback(() => {
    setPendingInvites([]);
    setPendingInvitesResolved(false);
    getInvites();
  }, [getInvites]);

  const isActive = pendingInvitesResolved && pendingInvites.length > 0 && !isExclusiveMode && state.user.authenticated && state.user.emailVerified;

  const render = useCallback(() => <InviteOffersDialogVR open={pendingInvites.length > 0} invites={pendingInvites} onClose={handleClose} />, [handleClose, pendingInvites]);

  useNotificationCandidate({
    id,
    priority,
    isActive,
    render,
  });

  return null;
};

export const pendingInvitesNotification = defineNotificationModule({
  id,
  Resolver: PendingInvitesNotificationResolver,
});
